import { ChevronRight } from "@procore/core-icons";
import { useMemo } from "react";

import type { Node, Tree } from "components/objectTree/types";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { highlightText } from "utils/textHighlight";

import { INDEX_SUFFIX_PATTERN, ITEM_HEIGHT_IOS } from "../constants";
import { formatNodeDisplayName } from "../utils/formatting";

export type MobileSearchResultsProps = {
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number>;
    searchTerm?: string;
    onSelectGroup: (path: string) => void;
};

function deriveBreadcrumb(path: string): string | null {
    const parts = path
        .split("/")
        .slice(0, -1)
        .map((s) => s.replace(INDEX_SUFFIX_PATTERN, "$1"));
    if (parts.length === 0) {
        return null;
    }
    if (parts.length <= 2) {
        return parts.join(" > ");
    }
    return `${parts[0]} > ... > ${parts[parts.length - 1]}`;
}

export function MobileSearchResults({
    tree,
    filteredObjectIds,
    searchTerm = "",
    onSelectGroup,
}: MobileSearchResultsProps) {
    const matches = useMemo(
        () =>
            Object.values(tree)
                .filter((n): n is Node<ModelTreeNodeData> => n.path !== "" && filteredObjectIds.has(n.data.object.id))
                .sort((a, b) => a.path.localeCompare(b.path)),
        [tree, filteredObjectIds],
    );

    return (
        <div style={{ height: "100%", overflowY: "auto" }}>
            {matches.map((node) => {
                const breadcrumb = deriveBreadcrumb(node.path);
                const displayName = formatNodeDisplayName({ node, parent: null, level: 0 });
                const isDrillable = !node.isLeaf;

                return (
                    <div
                        key={node.path}
                        onClick={isDrillable ? () => onSelectGroup(node.path) : undefined}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: ITEM_HEIGHT_IOS,
                            padding: "0 16px",
                            gap: 10,
                            borderBottom: "1px solid rgba(172, 181, 185, 0.32)",
                            userSelect: "none",
                            cursor: isDrillable ? "pointer" : "default",
                        }}
                    >
                        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                            {breadcrumb && (
                                <span
                                    data-testid="search-result-breadcrumb"
                                    style={{
                                        fontSize: 12,
                                        color: "#6B7280",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {breadcrumb}
                                </span>
                            )}
                            <span
                                style={{
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 2,
                                    overflow: "hidden",
                                    overflowWrap: "anywhere",
                                    fontSize: 18,
                                    fontWeight: 600,
                                    color: "#171a1c",
                                    letterSpacing: "-0.28px",
                                    lineHeight: "20px",
                                }}
                            >
                                {searchTerm ? highlightText(displayName, searchTerm) : displayName}
                            </span>
                        </span>
                        {isDrillable && (
                            <ChevronRight style={{ flexShrink: 0, width: 20, height: 20, color: "#ACB5B9" }} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
